import React from 'react';

import { Button, Modal, Stack, Text, Title } from '@mantine/core';
import { AxiosError, AxiosResponse } from 'axios';
import { Dayjs } from 'dayjs';
import { UseMutationResult } from 'react-query';
import { AthleteKiloClass, ErrorMessage, KiloClassAthleteRetrieve } from 'types';
import notificationHandler from 'utils/notifications';

interface ModalLeaveWaitlistProps {
  opened: boolean;
  athleteKiloClassId: string;
  leaveWaitlistMutation: UseMutationResult<
    AxiosResponse<null | AthleteKiloClass>,
    AxiosError<ErrorMessage>,
    string
  >;
  kiloClass: KiloClassAthleteRetrieve;
  classStartDateTime: Dayjs;
  waitlistOrder: number | null;
  handleClose: () => void;
}

const ModalLeaveWaitlist = ({
  opened,
  athleteKiloClassId,
  leaveWaitlistMutation,
  kiloClass,
  classStartDateTime,
  waitlistOrder,
  handleClose,
}: ModalLeaveWaitlistProps): JSX.Element => {
  const { successNotification, errorNotification } = notificationHandler();

  const className = kiloClass.kiloClassType.name;
  const spot = waitlistOrder ? ` (#${waitlistOrder})` : '';
  const classDateTime = `${classStartDateTime.format('dddd, MMMM D')} at ${classStartDateTime.format(
    'h:mma',
  )}`;

  const handleLeaveWaitlist = (): void => {
    leaveWaitlistMutation.mutate(athleteKiloClassId, {
      onSuccess: () => {
        successNotification('You left the waitlist', `${className} on ${classDateTime}`);
        handleClose();
      },
      onError: error => {
        errorNotification('Could not leave the waitlist', error.response?.data.message);
      },
    });
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={handleClose}
      title={<Title order={4}>Leave the waitlist?</Title>}
      size='md'
    >
      <Stack px='md'>
        <Text>{`You'll lose your spot${spot}. You can rejoin later if there's still room.`}</Text>
        <Button
          mt='md'
          onClick={handleLeaveWaitlist}
          variant='outline'
          loading={leaveWaitlistMutation.isLoading}
        >
          Yes, leave waitlist
        </Button>
        <Button mb='md' onClick={handleClose}>
          Keep my spot
        </Button>
      </Stack>
    </Modal>
  );
};

export default ModalLeaveWaitlist;
